import {
  getMaxDiscountPercentage,
  getSingleSpecValue,
  getStandaloneProductVariantById
} from "../../../commons/libs/specs";
import { BicycleSpecKey } from "../../../commons/specs/bicycle";
import { MotorcycleSpecKey } from "../../../commons/specs/motorcycle";
import {
  Product,
  ProductId,
  ProductKey,
  ProductSpecKey,
  ProductType,
  SpecMap,
  StandaloneProductVariant
} from "../../../commons/specs/product";

interface ProductPropertiesOptions {
  variantId?: ProductId;
}

type CommonTrackedSpecKey =
  | ProductSpecKey.ModelName
  | ProductSpecKey.ModelYear
  | ProductSpecKey.BrandId
  | ProductSpecKey.BrandName
  | ProductSpecKey.BrandKey
  | ProductSpecKey.CategoryKey
  | ProductSpecKey.CategoryName
  | ProductSpecKey.ProductContentKey
  | ProductSpecKey.Price
  | ProductSpecKey.Rrp
  | ProductSpecKey.OriginalPrice
  | ProductSpecKey.Currency
  | ProductSpecKey.DiscountPercentage;

type BicycleTrackedSpecKey =
  | BicycleSpecKey.BikeTypeKey
  | BicycleSpecKey.FrameTypeKey
  | BicycleSpecKey.MaterialKey
  | BicycleSpecKey.WheelSize
  | BicycleSpecKey.GearCount
  | BicycleSpecKey.GearTypeKey
  | BicycleSpecKey.GearBrandKey
  | BicycleSpecKey.GearSeriesKey
  | BicycleSpecKey.BrakeTypeKey
  | BicycleSpecKey.HasEngine
  | BicycleSpecKey.EngineBrand
  | BicycleSpecKey.EngineSpeed
  | BicycleSpecKey.BatteryCapacity
  | BicycleSpecKey.Weight;

type MotorcycleTrackedSpecKey =
  | MotorcycleSpecKey.BikeTypeKey
  | MotorcycleSpecKey.IsElectric
  | MotorcycleSpecKey.EngineBrand
  | MotorcycleSpecKey.Displacement
  | MotorcycleSpecKey.PowerKw
  | MotorcycleSpecKey.CylinderCount
  | MotorcycleSpecKey.EngineStroke
  | MotorcycleSpecKey.Cooling
  | MotorcycleSpecKey.TransmissionKey
  | MotorcycleSpecKey.GearCount
  | MotorcycleSpecKey.WeightEmpty
  | MotorcycleSpecKey.SeatHeight
  | MotorcycleSpecKey.Co2emissions
  | MotorcycleSpecKey.FuelConsumption;

type BaseProductTrackingProperties<Type extends ProductType> = Pick<SpecMap[Type], CommonTrackedSpecKey> & {
  [ProductKey.ProductId]: ProductId;
  [ProductKey.ProductType]: Type;
  [ProductKey.VariantId]?: ProductId;
};

export type BicycleProductTrackingProperties = BaseProductTrackingProperties<ProductType.Bicycle> &
  Pick<SpecMap[ProductType.Bicycle], BicycleTrackedSpecKey>;

export type MotorcycleProductTrackingProperties = BaseProductTrackingProperties<ProductType.Motorcycle> &
  Pick<SpecMap[ProductType.Motorcycle], MotorcycleTrackedSpecKey>;

export type ProductTrackingProperties = BicycleProductTrackingProperties | MotorcycleProductTrackingProperties;

export const getProductProperties = (
  product: Product,
  { variantId }: ProductPropertiesOptions = {}
): ProductTrackingProperties => {
  const productType = product[ProductKey.ProductType];
  const variant = getStandaloneProductVariantById(product, variantId);
  // When a specific variant is selected, read spec values from it (variant specs already
  // include the product-level specs via `getStandaloneProductVariant`). Otherwise fall back
  // to the product, where `getSingleSpecValue` reads from the first variant.
  const source = variant ?? product;

  const discountPercentage = variant ? variant[ProductSpecKey.DiscountPercentage] : getMaxDiscountPercentage(product);

  const commonProperties = {
    // --- identity ---
    productId: product[ProductKey.ProductId],
    ...(variantId ? { variantId } : {}),

    // --- model & brand ---
    modelName: getSingleSpecValue(ProductSpecKey.ModelName, source),
    modelYear: getSingleSpecValue(ProductSpecKey.ModelYear, source),
    brandId: getSingleSpecValue(ProductSpecKey.BrandId, source),
    brandName: getSingleSpecValue(ProductSpecKey.BrandName, source),
    brandKey: getSingleSpecValue(ProductSpecKey.BrandKey, source),

    // --- taxonomy ---
    productType,
    categoryKey: getSingleSpecValue(ProductSpecKey.CategoryKey, source),
    categoryName: getSingleSpecValue(ProductSpecKey.CategoryName, source),
    productContentKey: getSingleSpecValue(ProductSpecKey.ProductContentKey, source),

    // --- price ---
    price: getSingleSpecValue(ProductSpecKey.Price, source),
    rrp: getSingleSpecValue(ProductSpecKey.Rrp, source),
    originalPrice: getSingleSpecValue(ProductSpecKey.OriginalPrice, source),
    currency: getSingleSpecValue(ProductSpecKey.Currency, source),
    discountPercentage
  };

  switch (productType) {
    case ProductType.Bicycle: {
      const bicycleSource = source as Product<ProductType.Bicycle> | StandaloneProductVariant<ProductType.Bicycle>;

      return {
        ...commonProperties,
        productType: ProductType.Bicycle,
        // --- bicycle taxonomy / segmentation ---
        bikeTypeKey: getSingleSpecValue(BicycleSpecKey.BikeTypeKey, bicycleSource),
        frameTypeKey: getSingleSpecValue(BicycleSpecKey.FrameTypeKey, bicycleSource),
        materialKey: getSingleSpecValue(BicycleSpecKey.MaterialKey, bicycleSource),
        wheelSize: getSingleSpecValue(BicycleSpecKey.WheelSize, bicycleSource),
        // --- drivetrain ---
        gearCount: getSingleSpecValue(BicycleSpecKey.GearCount, bicycleSource),
        gearTypeKey: getSingleSpecValue(BicycleSpecKey.GearTypeKey, bicycleSource),
        gearBrandKey: getSingleSpecValue(BicycleSpecKey.GearBrandKey, bicycleSource),
        gearSeriesKey: getSingleSpecValue(BicycleSpecKey.GearSeriesKey, bicycleSource),
        brakeTypeKey: getSingleSpecValue(BicycleSpecKey.BrakeTypeKey, bicycleSource),
        // --- e-bike ---
        hasEngine: getSingleSpecValue(BicycleSpecKey.HasEngine, bicycleSource),
        engineBrand: getSingleSpecValue(BicycleSpecKey.EngineBrand, bicycleSource),
        engineSpeed: getSingleSpecValue(BicycleSpecKey.EngineSpeed, bicycleSource),
        batteryCapacity: getSingleSpecValue(BicycleSpecKey.BatteryCapacity, bicycleSource),
        // --- physical ---
        weight: getSingleSpecValue(BicycleSpecKey.Weight, bicycleSource)
      };
    }
    case ProductType.Motorcycle: {
      const motorcycleSource = source as
        Product<ProductType.Motorcycle> | StandaloneProductVariant<ProductType.Motorcycle>;

      return {
        ...commonProperties,
        productType: ProductType.Motorcycle,
        // --- motorcycle taxonomy / segmentation ---
        bikeTypeKey: getSingleSpecValue(MotorcycleSpecKey.BikeTypeKey, motorcycleSource),
        // --- engine ---
        isElectric: getSingleSpecValue(MotorcycleSpecKey.IsElectric, motorcycleSource),
        engineBrand: getSingleSpecValue(MotorcycleSpecKey.EngineBrand, motorcycleSource),
        displacement: getSingleSpecValue(MotorcycleSpecKey.Displacement, motorcycleSource),
        powerKw: getSingleSpecValue(MotorcycleSpecKey.PowerKw, motorcycleSource),
        cylinderCount: getSingleSpecValue(MotorcycleSpecKey.CylinderCount, motorcycleSource),
        engineStroke: getSingleSpecValue(MotorcycleSpecKey.EngineStroke, motorcycleSource),
        cooling: getSingleSpecValue(MotorcycleSpecKey.Cooling, motorcycleSource),
        // --- drivetrain ---
        transmissionKey: getSingleSpecValue(MotorcycleSpecKey.TransmissionKey, motorcycleSource),
        gearCount: getSingleSpecValue(MotorcycleSpecKey.GearCount, motorcycleSource),
        // --- physical / regulatory ---
        weightEmpty: getSingleSpecValue(MotorcycleSpecKey.WeightEmpty, motorcycleSource),
        seatHeight: getSingleSpecValue(MotorcycleSpecKey.SeatHeight, motorcycleSource),
        co2emissions: getSingleSpecValue(MotorcycleSpecKey.Co2emissions, motorcycleSource),
        fuelConsumption: getSingleSpecValue(MotorcycleSpecKey.FuelConsumption, motorcycleSource)
      };
    }
    default:
      throw new Error(`Unsupported product type: ${productType}`);
  }
};
